import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { track } from '@/lib/analytics'
import { scrollToAnchor } from '@/lib/scrollToAnchor'
import { localeFromPath } from '@/i18n/locale'
import { isHomePath } from '@/lib/sections'

/**
 * Плавающая CTA-кнопка снизу на мобайле.
 *
 * Порог был «прокрутить 0,8 экрана», и это стоило конверсии: замер на
 * 375×590 и 375×667 показал НОЛЬ кликабельных конверсионных элементов
 * над сгибом — кнопка в hero ниже сгиба, мобильная CTA в закрытом
 * бургере, sticky ещё скрыт. Кнопка появлялась только на экранах от
 * ~844px. Теперь порог 120px: касание скролла — и CTA на месте.
 *
 * Обратная сторона постоянной кнопки: бар высотой ~89px перекрывал бы
 * поля и сабмит формы. Поэтому пока форма во вьюпорте — sticky уходит:
 * у формы ровно один primary-CTA, её собственный.
 */
export function StickyCta() {
  const [scrolled, setScrolled] = useState(false)
  const [formInView, setFormInView] = useState(false)
  const { pathname } = useLocation()
  const isHome = isHomePath(pathname)
  const isEn = localeFromPath(pathname) === 'en'
  const label = isEn ? 'Book AI diagnostics' : 'Записаться на диагностику'
  // Голая кнопка не отвечала на «во что это мне обойдётся».
  const note = isEn ? 'free · 30–90 minutes' : 'бесплатно · 30–90 минут'
  const visible = scrolled && !formInView

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Форма в кадре → прячем бар, чтобы он не закрывал поля и сабмит.
  useEffect(() => {
    const form = document.getElementById('lead-form')
    if (!form) return
    const io = new IntersectionObserver(([e]) => setFormInView(e.isIntersecting), { threshold: 0.25 })
    io.observe(form)
    return () => io.disconnect()
  }, [pathname])

  // На главной — прямой скролл к форме на КАЖДЫЙ тап (preventDefault).
  // Раньше полагались на нативный hash-jump / ScrollToHash через смену
  // hash — но на длинной странице это давало «недоезд», а повторный тап
  // (hash уже #final-cta) не делал ничего. Прямой scrollToAnchor чинит оба.
  // На внутренних — router Link на /#final-cta (ScrollToHash доскроллит
  // после перехода на главную).
  const onHomeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    track('sticky_cta_click')
    // Ведём прямо на поля формы (#lead-form), а не на заголовок секции
    // #final-cta — на мобиле между ними ~800px ценника, и пользователь,
    // тапнувший «Записаться», должен увидеть форму, а не текст.
    scrollToAnchor('lead-form')
  }

  return (
    <div
      aria-hidden={!visible}
      // inert убирает поддерево из tab-order и a11y-tree пока CTA скрыт.
      // tabIndex={-1} на ссылке — bullet-belt для браузеров без inert.
      // Без этого Lighthouse aria-hidden-focus падает на скрытом блоке.
      // @ts-expect-error — inert уже широко поддержан, но React-types не знают
      inert={visible ? undefined : ''}
      className={`md:hidden fixed inset-x-0 bottom-0 z-30 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-bg/95 backdrop-blur border-t border-divider transition-transform ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {isHome ? (
        <a
          href="#lead-form"
          className="btn-primary w-full no-underline"
          tabIndex={visible ? 0 : -1}
          onClick={onHomeClick}
        >
          <span className="flex flex-col items-center leading-tight">
            <span>{label}</span>
            <span className="text-[0.6875rem] font-normal opacity-70">{note}</span>
          </span>
        </a>
      ) : (
        <Link
          to="/#final-cta"
          className="btn-primary w-full no-underline"
          tabIndex={visible ? 0 : -1}
          onClick={() => track('sticky_cta_click')}
        >
          <span className="flex flex-col items-center leading-tight">
            <span>{label}</span>
            <span className="text-[0.6875rem] font-normal opacity-70">{note}</span>
          </span>
        </Link>
      )}
    </div>
  )
}
